import { Check, CheckCircle2, CircleAlert, Star, X } from "lucide-react";
import type {
  PoliticsAnswerResult,
  PoliticsOptionKey,
  PoliticsQuestionPrompt,
  PoliticsQuestionSolution,
} from "@/lib/politics";
import { Button, Tag, cx } from "./primitives";

export function PoliticsQuestionHeader({ question }: { question: PoliticsQuestionPrompt }) {
  return (
    <header className="politics-question__header">
      <div className="tag-list">
        <Tag tone="accent">{question.type === "single_choice" ? "单选题" : "多选题"}</Tag>
        <Tag>{question.section.name}</Tag>
      </div>
      <span>第 {question.sourceNumber} 题</span>
    </header>
  );
}

export function PoliticsQuestionOptions({
  question,
  selected,
  result,
  disabled = false,
  onChange,
}: {
  question: PoliticsQuestionPrompt;
  selected: PoliticsOptionKey[];
  result?: PoliticsAnswerResult | undefined;
  disabled?: boolean | undefined;
  onChange?: ((keys: PoliticsOptionKey[]) => void) | undefined;
}) {
  const multiple = question.type === "multiple_choice";
  const correctKeys = new Set(result?.correctOptionKeys ?? []);
  const selectedKeys = new Set(result?.selectedOptionKeys ?? selected);
  return (
    <fieldset className="politics-options" disabled={disabled || Boolean(result)}>
      <legend className="sr-only">{multiple ? "选择所有正确选项" : "选择一个正确选项"}</legend>
      {question.options.map((option) => {
        const checked = selectedKeys.has(option.key);
        const correct = correctKeys.has(option.key);
        const incorrectSelection = Boolean(result) && checked && !correct;
        return (
          <label
            className={cx(
              "politics-option",
              checked && "politics-option--selected",
              Boolean(result) && correct && "politics-option--correct",
              incorrectSelection && "politics-option--incorrect",
            )}
            key={option.key}
          >
            <input
              type={multiple ? "checkbox" : "radio"}
              name={`politics-question-${question.id}`}
              value={option.key}
              checked={checked}
              onChange={() => {
                if (!onChange || result) return;
                if (!multiple) {
                  onChange([option.key]);
                  return;
                }
                onChange(checked ? selected.filter((key) => key !== option.key) : [...selected, option.key]);
              }}
            />
            <span className="politics-option__key">{option.key}</span>
            <span className="politics-option__text">{option.text}</span>
            {result && correct ? <Check aria-label="正确选项" /> : null}
            {incorrectSelection ? <X aria-label="错误选择" /> : null}
          </label>
        );
      })}
    </fieldset>
  );
}

export function PoliticsSolution({
  solution,
  result,
}: {
  solution: PoliticsQuestionSolution;
  result?: PoliticsAnswerResult;
}) {
  return (
    <section className={cx("politics-solution", result && (result.correct ? "politics-solution--correct" : "politics-solution--wrong"))}>
      {result ? (
        <header>
          {result.correct ? <CheckCircle2 /> : <CircleAlert />}
          <div>
            <p className="section-label">{result.correct ? "回答正确" : "回答错误"}</p>
            <h2>正确答案：{solution.correctOptionKeys.join("、")}</h2>
          </div>
        </header>
      ) : (
        <header><div><p className="section-label">题目解析</p><h2>正确答案：{solution.correctOptionKeys.join("、")}</h2></div></header>
      )}
      <p className="politics-solution__explanation">{solution.explanation}</p>
      <dl className="politics-solution__meta">
        {solution.knowledgePoint ? <div><dt>考点</dt><dd>{solution.knowledgePoint}</dd></div> : null}
        {solution.reference ? <div><dt>出处</dt><dd>{solution.reference}</dd></div> : null}
        {solution.tip ? <div><dt>点拨</dt><dd>{solution.tip}</dd></div> : null}
      </dl>
    </section>
  );
}

export function PoliticsQuestionCard({
  question,
  selected = [],
  result,
  disabled,
  favoriteBusy,
  onSelectionChange,
  onFavoriteChange,
}: {
  question: PoliticsQuestionPrompt;
  selected?: PoliticsOptionKey[];
  result?: PoliticsAnswerResult | undefined;
  disabled?: boolean | undefined;
  favoriteBusy?: boolean | undefined;
  onSelectionChange?: ((keys: PoliticsOptionKey[]) => void) | undefined;
  onFavoriteChange?: ((favorite: boolean) => void) | undefined;
}) {
  return (
    <article className="politics-question">
      <PoliticsQuestionHeader question={question} />
      <div className="politics-question__title-row">
        <h1>{question.stem}</h1>
        {onFavoriteChange ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cx("politics-favorite-button", question.favorite && "politics-favorite-button--active")}
            aria-pressed={question.favorite}
            disabled={favoriteBusy}
            onClick={() => onFavoriteChange(!question.favorite)}
          >
            <Star fill={question.favorite ? "currentColor" : "none"} />
            {question.favorite ? "已收藏" : "收藏"}
          </Button>
        ) : null}
      </div>
      <PoliticsQuestionOptions
        question={question}
        selected={selected}
        result={result}
        disabled={disabled}
        onChange={onSelectionChange}
      />
    </article>
  );
}
